import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useHistory } from 'react-router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { IconList } from '../components/IconList/IconList';
import IconListItem from '../components/IconList/IconListItem';
import BaseLayoutCta from '../components/BaseLayoutCta/BaseLayoutCta';
import { PAGES } from '../constants/pages';
import Paragraph from '../components/Paragraph/Paragraph';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import { JWT_TOKEN } from '../constants/localStorage';
import { logoutUser } from '../store/actions/user/userActions';
import {
  APPLICATION_TYPE,
  ACCOUNT_TYPE,
  LANDING_PAGE_URL,
  OFFER_CODE,
  REGISTRATION_FLOW_TYPE,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  SALES_ASSISTED_QUERY_DATA,
} from '../constants/sessionStorage';
import { REGISTRATION_FLOW_TYPES } from '../util/constants';
import { isAipRegistrationFlow } from '../util/helpers/registrationFlowTypeHelper';
import { postTrackingDetails } from '../store/actions/affiliate/afilliateActions';
import {
  getAffParam,
  getCJeventParam,
  getPromotionParam,
} from '../util/helpers/referrerHelpers';
import useQuery from '../util/hooks/useQuery';
import CashPaymentBag from '../components/Icons/CashPaymentBag';
import BaseLayout from '../components/BaseLayout';
import { Heading } from '../components/Heading';
import { selectSettings } from '../store/selectors/settingsSelectors';
import themeColors from '../assets/styles/themeColors';
import VaultLock from '../components/Icons/VaultLock';
import Clock from '../components/Icons/ClockIcon';

export const WelcomeLoginLink = styled.a`
  color: ${themeColors.colorLink};
  text-decoration: none;
  font-weight: 600;
`;

const WelcomeFluent = ({ registrationFlowType }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const { logonUid, username, brokerCode } = query;
  const referrerUrl = document.referrer;
  const landingPageUrl = window.location.href;
  const referrerAff = getAffParam();
  const referrerCJEvent = getCJeventParam();
  const referrerPromotionCode = getPromotionParam();
  const settings = useSelector(selectSettings);

  const { BrokerName, BrokerCode } = settings;

  const welcomeDescriptions = [
    {
      title: t('welcomeFluent.applyInMinutes.title'),
      description: t('welcomeFluent.applyInMinutes.description', {
        brokerCode: BrokerCode,
      }),
      Icon: Clock,
    },
    {
      title: isAipRegistrationFlow(registrationFlowType)
        ? t('welcomeFluent.setIt.title')
        : t('welcomeFluent.brokerage.title'),
      description: isAipRegistrationFlow(registrationFlowType)
        ? t('welcomeFluent.setIt.description', {
            brokerCode: BrokerCode,
          })
        : t('welcomeFluent.brokerage.description', {
            brokerCode: BrokerCode,
          }),
      Icon: CashPaymentBag,
    },
    {
      title: t('welcomeFluent.international.title'),
      description: t('welcomeFluent.international.description'),
      Icon: VaultLock,
    },
  ];

  useEffect(() => {
    const JwtToken = retrieveFromSessionStorage(JWT_TOKEN);

    if (JwtToken) {
      dispatch(logoutUser());
    }
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname.includes('aip')) {
      storeInSessionStorage(
        REGISTRATION_FLOW_TYPE,
        REGISTRATION_FLOW_TYPES.AIP,
      );
      storeInSessionStorage(APPLICATION_TYPE, 'Individual');
      removeFromSessionStorage(ACCOUNT_TYPE);
      return;
    }

    storeInSessionStorage(
      REGISTRATION_FLOW_TYPE,
      REGISTRATION_FLOW_TYPES.REGULAR,
    );
  }, [location.pathname]);

  useEffect(() => {
    if (brokerCode && logonUid && username) {
      storeInSessionStorage(IS_SALES_ASSESSTED_REGISTRATION_FLOW, true);
      storeInSessionStorage(SALES_ASSISTED_QUERY_DATA, {
        brokerCode,
        logonUid,
        username,
      });
    }
  }, [brokerCode, logonUid, username]);

  useEffect(() => {
    const { aff, p, cjevent, offer, a } = query;
    if (offer) {
      storeInSessionStorage(OFFER_CODE, offer);
    }
    storeInSessionStorage(LANDING_PAGE_URL, landingPageUrl);
    setTimeout(() => {
      dispatch(
        postTrackingDetails({
          data: {
            AffiliateCode: aff || referrerAff || a,
            CJEvent: cjevent || referrerCJEvent,
            PromotionCode: p || referrerPromotionCode,
            ReferrerUrl: referrerUrl,
          },
        }),
      );
    }, 1000);
  }, []); //eslint-disable-line

  return (
    <BaseLayout noLogoBox>
      <Helmet>
        <title>
          {t('welcomeFluent.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Heading fontWeight={700} mb={8}>
        {t('welcomeFluent.title', { brokerName: BrokerName })}
      </Heading>
      <Paragraph mb={8} color={themeColors.colorTextXLLight}>
        {t('welcomeFluent.subtitle')}
      </Paragraph>
      {isAipRegistrationFlow(registrationFlowType) && (
        <Paragraph mb={8} color={themeColors.colorTextXLLight}>
          {t('welcomeFluent.metalstreamSubtitle', { brokerCode: BrokerCode })}
        </Paragraph>
      )}
      <IconList mt={20}>
        {welcomeDescriptions.map(({ title, description, Icon }) => (
          <IconListItem
            key={title}
            title={title}
            description={description}
            Icon={Icon}
          />
        ))}
      </IconList>
      <BaseLayoutCta
        indent
        buttonProps={{
          title: t('welcomeFluent.getStarted'),
          onClick: () =>
            history.push(
              isAipRegistrationFlow(registrationFlowType)
                ? PAGES.CREATE_ACCOUNT
                : PAGES.CREATE_APPLICATION.MAIN,
            ),
        }}
        content={
          <Paragraph>
            {t('welcomeFluent.haveApplication')}{' '}
            <WelcomeLoginLink
              onClick={(e) => {
                e.preventDefault();
                history.push(PAGES.LOGIN_PATH);
              }}
            >
              {t('welcomeFluent.login')}
            </WelcomeLoginLink>
          </Paragraph>
        }
      />
    </BaseLayout>
  );
};

WelcomeFluent.propTypes = {
  registrationFlowType: PropTypes.string,
};

export default WelcomeFluent;
